import { t, getCurrentLanguage } from '@/utils/i18n'
import { withLangPrefix, type Lang } from '@/utils/lang'
import type { IRPost } from '@/types/ir'

export function irLink(path: string, label: string, className: string): HTMLAnchorElement {
  const link = document.createElement('a')
  link.href = withLangPrefix(path, getCurrentLanguage())
  link.textContent = label
  link.className = className
  link.addEventListener('click', event => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    window.dispatchEvent(new CustomEvent('navigate', { detail: path }))
  })
  return link
}

export function irDate(date: string, language: Lang = getCurrentLanguage()): string {
  return new Intl.DateTimeFormat(language === 'ko' ? 'ko-KR' : 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Asia/Seoul',
  }).format(new Date(`${date}T00:00:00+09:00`))
}

export function IRContent(post: IRPost, html: string, { showAttachment = true, language = getCurrentLanguage() }: { showAttachment?: boolean; language?: Lang } = {}): HTMLElement {
  const content = document.createElement('div')
  const body = document.createElement('div')
  body.className = 'prose prose-slate max-w-none prose-p:leading-relaxed prose-li:leading-relaxed prose-strong:text-slate-900 break-words'
  // Markdown is rendered at build time with raw HTML disabled; shared with the popup.
  body.innerHTML = html
  const centered = new Set(post.centeredParagraphs?.[language] ?? [])
  for (const paragraph of body.querySelectorAll('p')) {
    if (centered.has(paragraph.textContent?.trim() ?? '')) paragraph.classList.add('text-center')
  }
  if (post.seal) {
    const company = post.seal.company[language]
    const companyLine = Array.from(body.querySelectorAll('p')).find(paragraph => paragraph.textContent?.trim() === company)
    if (companyLine) {
      // Match the PDF's seal-to-type ratio; keep room above/below and to the right.
      companyLine.classList.add('py-[1.5em]')
      const signature = document.createElement('span')
      signature.className = 'inline-block pr-[4.1em]'
      const label = document.createElement('span')
      label.className = 'relative inline-block whitespace-nowrap'
      label.textContent = company
      const seal = document.createElement('img')
      seal.src = post.seal.image
      seal.alt = t('ir.companySeal', language)
      seal.className = '!m-0 absolute left-full top-1/2 h-[4.5em] w-[4.5em] max-w-none -translate-x-[0.4em] -translate-y-1/2 object-contain mix-blend-multiply'
      seal.decoding = 'async'
      seal.dataset.irSeal = ''
      label.appendChild(seal)
      signature.appendChild(label)
      companyLine.replaceChildren(signature)
    }
  }
  content.appendChild(body)
  if (language === 'en') {
    const original = document.createElement('p')
    original.className = 'mt-8 text-sm text-slate-600 leading-relaxed'
    const originalLink = document.createElement('a')
    originalLink.href = withLangPrefix(`/ir/${post.slug}`, 'ko')
    originalLink.setAttribute('data-preserve-locale', '')
    originalLink.textContent = t('ir.koreanOriginal', language)
    originalLink.className = 'underline underline-offset-4 text-orange-700'
    // Cross-language navigation reloads the persistent Header/Footer.
    original.append(t('ir.originalNote', language), ' ', originalLink)
    content.appendChild(original)
  }
  if (showAttachment && post.attachment) {
    const attachment = document.createElement('a')
    attachment.href = post.attachment
    attachment.download = ''
    attachment.textContent = t('ir.downloadOriginal', language).replace('{type}', post.attachment.endsWith('.pdf') ? 'PDF' : 'HWP')
    attachment.className = 'mt-8 inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-700'
    content.appendChild(attachment)
  }
  return content
}
